import { useCallback } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { ehandoverAPI, systemAPI } from '../../../apis';
import { initValue } from '../../../utils';
import {
  CHECKLIST_FAMILY_TAB_IDS,
  COMPLETE_CHECKLIST_STATUS,
  EHANDOVER_TAB_IDS,
  FIELD_FROM_BITRIX_API,
  INCOMPLETE_CHECKLIST_STATUS,
  INITIAL_TOTAL_SELECT,
  SELECTIVE_TOTAL_REFRESH_MAP,
  TAB_LIST_API_TOTAL_ATTEMPTS,
} from '../constants/config';

function roundUpToNearest10(time) {
  if(time === null){
    return time;
  }
  const momentTime = moment(time);
  const remainder = 10 - (momentTime.minute() % 10);

  // If remainder is 10, the time is already on a 10-min interval, 
  // so we don't need to add anything.
  return moment(time).clone().add(remainder, "m").subtract(1, "s").milliseconds(0);
}

// 统一承接 EHandover 页面里“列表请求 + total 刷新”的逻辑。
// index.js 更聚焦在页面布局、事件接线和场景编排，
export default function useEhandoverListManager({
  addTotal,
  appendTabListState,
  currentTabId,
  endDate,
  followUpFilter,
  isLogin,
  loginInfo,
  notificationTaskLength,
  reduceTotal,
  refreshNotificationTotal,
  replaceTabListState,
  requiredAcknowLedge,
  retryApiCall,
  search,
  setFollowUpTotal,
  sortOrder,
  startDate,
  tabData,
  tagNames,
  updateTabTotalState,
}) {
  // 统一生成 bitrix 列表接口的 filter。
  // 这个 helper 只负责把当前公共筛选拼成后端可识别的参数，不直接发请求。
  const handleFilter = useCallback(
    ({ search, startDate, endDate, tag }) => {
      let filter = {};
      const endDateIsToday = moment(endDate).isSame(moment(), 'day');

      if (search) {
        filter.title = `%${search}%`;
      }
      if (tag) {
        filter.TAG = tag;
      }
      if (startDate) {
        filter['>=DATE_START'] = startDate;
        filter['<=DATE_START'] = roundUpToNearest10(endDateIsToday ? moment().add(1, 'minutes').format('yyyy-MM-DD HH:mm') : endDate);
      }
      if (requiredAcknowLedge) {
        filter.mark = 'N';
      }

      return filter;
    },
    [requiredAcknowLedge]
  );

  // 把 bitrix 原始任务转换成页面列表真正消费的数据结构。
  // 这里保持原有字段映射不变，避免影响列表 UI 和后续的 note 交互。
  const revampTaskData = useCallback(
    (taskList) => {
      if (!Array.isArray(taskList) || taskList.length === 0) {
        return [];
      }

      return _.map(taskList, (item) => ({
        ...item,
        TASK_ID: item.id,
        name: item.creator.name,
        time: moment(item.dateStart).isValid() ? item.dateStart : '',
        context: item.description,
        comment: false,
        commentsTotal: item.commentsCount ?? 0,
        pin: item.priority !== '1',
        addTotal,
        reduceTotal,
        metadata: item,
      }));
    },
    [addTotal, reduceTotal]
  );

  // Deleted tab 的接口结构和普通 task 一致，但列表展示字段更精简。
  // 这里在请求完成后直接转成页面真正消费的数据，避免 index.js 再做一层二次 map。
  const formatDeletedTaskData = useCallback((taskList) => {
    if (!Array.isArray(taskList) || taskList.length === 0) {
      return [];
    }

    return _.map(taskList, (item) => ({
      TASK_ID: item.id,
      name: item.creator.name,
      time: moment(item.dateStart).isValid() ? item.dateStart : '',
      context: item.description,
      tags: item.tags,
      comment: false,
      pin: item.priority !== '1',
      commentsTotal: item.commentsCount ?? 0,
      ufTaskWebdavFiles: item.ufTaskWebdavFiles,
      deletedDate: item.deletedDate,
      deletedBy: item.deletedBy,
      metadata: item,
    }));
  }, []);

  // Scheduled tab 需要额外保留 recurrence 和 updater 信息。
  // 这类结构如果留到页面层再组装，会让 data flow 来回跳，所以统一前移到列表 hook。
  const formatScheduledTaskData = useCallback((taskList) => {
    if (!Array.isArray(taskList) || taskList.length === 0) {
      return [];
    }

    return _.map(taskList, (item) => ({
      TASK_ID: item.id,
      name: item.creator.name,
      time: moment(item.dateStart).isValid() ? item.dateStart : '',
      context: item.description,
      tags: item.tags,
      postday: item?.postDay,
      comment: false,
      pin: item.priority !== '1',
      commentsTotal: item.commentsCount ?? 0,
      ufTaskWebdavFiles: item.ufTaskWebdavFiles,
      deletedDate: item.deletedDate,
      deletedBy: item.deletedBy,
      updater: item.updater,
      metadata: item,
      customRecurrenceConfig: {
        customRecurrence: item?.customRecurrence,
        intervalValue: item?.intervalValue,
        intervalType: item?.intervalType,
        daysOfWeek: item?.daysOfWeek ? item?.daysOfWeek?.split(',') : [],
        monthDayOfWeek: item?.monthDayOfWeek,
        monthRepeatType: item?.monthRepeatType,
        endDate: item?.endDate ? moment(item?.endDate).format('yyyy-MM-DD HH:mm:ss') : null,
      },
      isUpdate: item?.isUpdate,
    }));
  }, []);

  // Notification tab 用的是另一套系统接口，这里直接转换成 NewNotes 可消费的结构。
  // 继续保留 pin 排序逻辑，避免页面层还要做额外的排序补偿。
  const formatNotificationList = useCallback((notificationList) => {
    if (!Array.isArray(notificationList) || notificationList.length === 0) {
      return [];
    }

    const list = _.map(notificationList, (item) => ({
      name: item.notificationTitle,
      time: moment(item.pushDate).isValid() ? moment(item.pushDate) : '',
      context: item.notificationDesc,
      tags:
        item?.tagNames?.length > 0
          ? _.map(item.tagNames, (tag, index) => ({
              tagDesc: tag,
              id: index,
            }))
          : null,
      notificationId: item.notificationId,
      pin: item.isPin === '1',
      createdBy: item.notificationFrom ?? item.createdBy,
      files: { imageIds: item.imageIds, fileIds: item.fileIds },
      ufTaskWebdavFiles: false,
      roomType: item.roomType,
      updater: item.updater,
      metadata: item,
    }));

    return _.orderBy(list, 'pin', 'desc');
  }, []);

  // checklist 家族共用的底层查询。
  // outstanding / completed / checklist 总 tab 都依赖它，所以单独抽出来避免规则散落。
  const fetchChecklistTasks = useCallback(
    async ({ status, start, search, startDate, endDate, tag, select = FIELD_FROM_BITRIX_API, totalOnly = false }) => {
      const filter = handleFilter({ search, startDate, endDate, tag });
      const { result } = await ehandoverAPI.getTaskList(
        {
          select,
          start,
          filter: { ...filter, title: `₩${filter.title ? filter.title : '%'}`, STATUS: status },
          order: sortOrder,
          corpId: loginInfo.corpID,
        },
        { isHideLoading: true }
      );

      return {
        total: initValue(result?.total, 0),
        next: result?.next ?? null,
        taskList: totalOnly ? [] : revampTaskData(result?.tasks),
      };
    },
    [handleFilter, loginInfo.corpID, revampTaskData, sortOrder]
  );

  // 根据 bucket 类型把 checklist 查询结果落回对应 state。
  // totalOnly 场景只更新标题 total，不覆盖列表数据和分页状态。
  const applyChecklistBucketState = useCallback(
    ({ bucket, start, taskList, total, next, totalOnly = false }) => {
      const targetTabId =
        bucket === 'outstanding' ? EHANDOVER_TAB_IDS.OUTSTANDING_TAB : EHANDOVER_TAB_IDS.COMPLETED_TAB;

      updateTabTotalState(targetTabId, total);
      if (totalOnly) {
        return;
      }

      if (start) {
        appendTabListState(targetTabId, { list: taskList, next });
        return;
      }

      replaceTabListState(targetTabId, { list: taskList, next });
    },
    [appendTabListState, replaceTabListState, updateTabTotalState]
  );

  // 统一根据当前场景把 tab 数据落回页面 state。
  // totalOnly 只更新标题 total；分页走 append；其它场景走 replace。
  const applyTabListState = useCallback(
    ({ tabId, list, next, total, totalOnly = false, start = false }) => {
      updateTabTotalState(tabId, total);
      if (totalOnly) {
        return;
      }

      if (start) {
        appendTabListState(tabId, { list, next });
        return;
      }

      replaceTabListState(tabId, { list, next });
    },
    [appendTabListState, replaceTabListState, updateTabTotalState]
  );

  // 统一刷新 checklist 家族的数据和 total。
  // 这是 checklist 相关最核心的联动入口，首屏、筛选、check/uncheck 都会复用这里。
  const refreshChecklistBuckets = useCallback(
    async ({ search, startDate, endDate, tag, select = FIELD_FROM_BITRIX_API, totalOnly = false }) => {
      const [incompleteResult, completeResult] = await Promise.all([
        fetchChecklistTasks({
          select,
          search,
          startDate,
          endDate,
          tag,
          totalOnly,
          status: INCOMPLETE_CHECKLIST_STATUS,
        }),
        fetchChecklistTasks({
          select,
          search,
          startDate,
          endDate,
          tag,
          totalOnly,
          status: COMPLETE_CHECKLIST_STATUS,
        }),
      ]);

      // 刷新 checklist 家族时，会一起更新 checklist 筛选器显示的 outstanding / completed 数量。
      setFollowUpTotal([
        { text: incompleteResult.total, value: '2' },
        { text: completeResult.total, value: '5' },
      ]);

      applyChecklistBucketState({
        bucket: 'outstanding',
        start: false,
        totalOnly,
        ...incompleteResult,
      });
      applyChecklistBucketState({
        bucket: 'complete',
        start: false,
        totalOnly,
        ...completeResult,
      });

      return { incompleteResult, completeResult };
    },
    [applyChecklistBucketState, fetchChecklistTasks, setFollowUpTotal]
  );

  // 刷新 All / Sent tab 列表或 total。
  const getAllTaskAPI = useCallback(
    async ({ start, search, startDate, endDate, tag, select = FIELD_FROM_BITRIX_API, totalOnly = false }) => {
      try {
        await retryApiCall(
          async () => {
            const { result } = await ehandoverAPI.getTaskList(
              {
                select,
                start,
                filter: { ...handleFilter({ search, startDate, endDate, tag }), STATUS: [2, 3, 4, 5] },
                order: sortOrder,
                corpId: loginInfo.corpID,
              },
              { isHideLoading: true }
            );

            applyTabListState({
              tabId: EHANDOVER_TAB_IDS.SENT_TAB,
              list: totalOnly ? [] : revampTaskData(result?.tasks),
              next: result?.next,
              start,
              total: result?.total,
              totalOnly,
            });
          },
          null,
          TAB_LIST_API_TOTAL_ATTEMPTS
        );
      } catch (error) {}
    },
    [applyTabListState, handleFilter, loginInfo.corpID, retryApiCall, revampTaskData, sortOrder]
  );

  // 刷新 checklist 总 tab。
  // 首次加载时会顺带同步 outstanding / completed 两个 bucket 的 total。
  const getChecklistTaskAPI = useCallback(
    async ({ start, search, startDate, endDate, tag, select = FIELD_FROM_BITRIX_API }) => {
      try {
        await retryApiCall(
          async () => {
            if (start) {
              const result = await fetchChecklistTasks({
                select,
                start,
                search,
                startDate,
                endDate,
                tag,
                status: followUpFilter === '2' ? INCOMPLETE_CHECKLIST_STATUS : COMPLETE_CHECKLIST_STATUS,
              });

              appendTabListState(EHANDOVER_TAB_IDS.CHECKLIST_TAB, { list: result?.taskList ?? [], next: result?.next });
              return;
            }

            const { incompleteResult, completeResult } = await refreshChecklistBuckets({
              search,
              startDate,
              endDate,
              tag,
              select,
            });

            if (followUpFilter === '2') {
              replaceTabListState(EHANDOVER_TAB_IDS.CHECKLIST_TAB, {
                list: incompleteResult.taskList,
                next: incompleteResult.next,
              });
            } else {
              replaceTabListState(EHANDOVER_TAB_IDS.CHECKLIST_TAB, {
                list: completeResult.taskList,
                next: completeResult.next,
              });
            }
          },
          null,
          TAB_LIST_API_TOTAL_ATTEMPTS
        );
      } catch (error) {}
    },
    [
      appendTabListState,
      fetchChecklistTasks,
      followUpFilter,
      refreshChecklistBuckets,
      replaceTabListState,
      retryApiCall,
    ]
  );

  // 刷新 Deleted tab 列表或 total。
  const getDeletedTaskAPI = useCallback(
    async ({ select = FIELD_FROM_BITRIX_API, search, start, startDate, endDate, tag, totalOnly = false }) => {
      try {
        await retryApiCall(
          async () => {
            const { result } = await ehandoverAPI.getDeletedTaskList(
              {
                select,
                start,
                filter: handleFilter({ search, startDate, endDate, tag }),
                order: sortOrder,
                corpId: loginInfo.corpID,
              },
              { isHideLoading: true }
            );

            applyTabListState({
              tabId: EHANDOVER_TAB_IDS.DELETED_TAB,
              list: totalOnly ? [] : formatDeletedTaskData(result?.tasks),
              next: result?.next,
              start,
              total: result?.total,
              totalOnly,
            });
          },
          null,
          TAB_LIST_API_TOTAL_ATTEMPTS
        );
      } catch (error) {}
    },
    [applyTabListState, formatDeletedTaskData, handleFilter, loginInfo.corpID, retryApiCall, sortOrder]
  );

  // 刷新 Scheduled tab 列表或 total。
  // Scheduled 的 endDate 有现有特殊规则，只在当前 active tab 是 schedule 时才真正参与过滤。
  const getPostdayTaskAPI = useCallback(
    async ({ select = FIELD_FROM_BITRIX_API, start, search, startDate, endDate, tag, totalOnly = false }) => {
      try {
        await retryApiCall(
          async () => {
            const { result } = await ehandoverAPI.getPostdayTaskList(
              {
                select,
                start,
                filter: handleFilter({
                  search,
                  startDate,
                  endDate: currentTabId === EHANDOVER_TAB_IDS.SCHEDULE_TAB ? endDate : null,
                  tag,
                }),
                order: sortOrder,
                corpId: loginInfo.corpID,
              },
              { isHideLoading: true }
            );

            applyTabListState({
              tabId: EHANDOVER_TAB_IDS.SCHEDULE_TAB,
              list: totalOnly ? [] : formatScheduledTaskData(result?.tasks),
              next: result?.next,
              start,
              total: result?.total,
              totalOnly,
            });
          },
          null,
          TAB_LIST_API_TOTAL_ATTEMPTS
        );
      } catch (error) {}
    },
    [applyTabListState, currentTabId, formatScheduledTaskData, handleFilter, loginInfo.corpID, retryApiCall, sortOrder]
  );

  // 刷新 Notification tab 列表或 total。
  const getNotificationList = useCallback(
    async ({ start, search, startDate, endDate, tag, totalOnly = false }) => {
      try {
        await retryApiCall(
          async () => {
            let size = 50;
            let dateEnd = null;

            if (start) {
              size = notificationTaskLength + 50;
            }
            if (endDate) {
              const endDateIsToday = moment(endDate).isSame(moment(), 'day');
              dateEnd = endDateIsToday
                ? moment(endDate, 'YYYY-MM-DD HH:mm').add(1, 'minutes').valueOf()
                : moment(endDate, 'YYYY-MM-DD HH:mm').valueOf();
            }

            const { result } = await systemAPI.getEhandNotification(
              {
                corpId: loginInfo.corpID ?? null,
                dateEnd,
                dateStart: startDate ? moment(startDate, 'YYYY-MM-DD HH:mm').valueOf() : null,
                pageNum: 1,
                pageSize: size,
                tagNames: tag ?? null,
                title: search ?? null,
                wardId: loginInfo.wardId,
                order: sortOrder,
              },
              { isHideLoading: true }
            );

            updateTabTotalState(EHANDOVER_TAB_IDS.NOTIFICATION_TAB, result.count);
            refreshNotificationTotal();
            if (totalOnly) {
              return;
            }

            replaceTabListState(EHANDOVER_TAB_IDS.NOTIFICATION_TAB, { list: formatNotificationList(result.data) });
          },
          null,
          TAB_LIST_API_TOTAL_ATTEMPTS
        );
      } catch (error) {}
    },
    [
      endDate,
      loginInfo.corpID,
      loginInfo.wardId,
      notificationTaskLength,
      formatNotificationList,
      refreshNotificationTotal,
      replaceTabListState,
      retryApiCall,
      sortOrder,
      updateTabTotalState,
    ]
  );

  // 刷新 outstanding checklist 列表或 total。
  const getOutstandingTask = useCallback(
    async ({ select = FIELD_FROM_BITRIX_API, search, start, startDate, endDate, tag, totalOnly = false }) => {
      try {
        await retryApiCall(
          async () => {
            const checklistResult = await fetchChecklistTasks({
              select,
              search,
              start,
              startDate,
              endDate,
              tag,
              totalOnly,
              status: INCOMPLETE_CHECKLIST_STATUS,
            });

            applyChecklistBucketState({ bucket: 'outstanding', start, totalOnly, ...checklistResult });
          },
          null,
          TAB_LIST_API_TOTAL_ATTEMPTS
        );
      } catch (error) {}
    },
    [applyChecklistBucketState, fetchChecklistTasks, retryApiCall]
  );

  // 刷新 completed checklist 列表或 total。
  const getCompleteTask = useCallback(
    async ({ select = FIELD_FROM_BITRIX_API, search, start, startDate, endDate, tag, totalOnly = false }) => {
      try {
        await retryApiCall(
          async () => {
            const checklistResult = await fetchChecklistTasks({
              select,
              search,
              start,
              startDate,
              endDate,
              tag,
              totalOnly,
              status: COMPLETE_CHECKLIST_STATUS,
            });

            applyChecklistBucketState({ bucket: 'complete', start, totalOnly, ...checklistResult });
          },
          null,
          TAB_LIST_API_TOTAL_ATTEMPTS
        );
      } catch (error) {}
    },
    [applyChecklistBucketState, fetchChecklistTasks, retryApiCall]
  );

  // 刷新 Pin tab 列表或 total。
  const getPinnedTask = useCallback(
    async ({ select = FIELD_FROM_BITRIX_API, search, start, startDate, endDate, tag, totalOnly = false }) => {
      try {
        await retryApiCall(
          async () => {
            const filter = handleFilter({ search, startDate, endDate, tag });
            const { result } = await ehandoverAPI.getPinTask(
              {
                select,
                start,
                filter: { ...filter, STATUS: [2, 3, 4, 5] },
                order: sortOrder,
                corpId: loginInfo.corpID,
              },
              { isHideLoading: true }
            );

            applyTabListState({
              tabId: EHANDOVER_TAB_IDS.PIN_TAB,
              // totalOnly 场景下 select 只传 ['ID']，这里不能再提前走 revamp，
              // 否则会因为 creator/dateStart 等字段缺失导致整个 total 更新链中断。
              list: totalOnly ? [] : revampTaskData(result.tasks),
              next: result?.next,
              start,
              total: result?.total,
              totalOnly,
            });
          },
          null,
          TAB_LIST_API_TOTAL_ATTEMPTS
        );
      } catch (error) {}
    },
    [applyTabListState, handleFilter, loginInfo.corpID, retryApiCall, revampTaskData, sortOrder]
  );

  // 刷新 My Draft tab 列表。
  // my draft 目前不参与 total 体系，但仍需要保留原来的列表加载逻辑。
  const getMyDraftTask = useCallback(
    async ({ start, search, startDate, endDate, tag, select = FIELD_FROM_BITRIX_API }) => {
      try {
        await retryApiCall(
          async () => {
            const { result } = await ehandoverAPI.getTaskDraft(
              {
                select,
                start,
                filter: handleFilter({ search, startDate, endDate, tag }),
                order: sortOrder,
                corpId: loginInfo.corpID,
              },
              { isHideLoading: true }
            );

            const revampMyDraftTask = revampTaskData(result.tasks);
            applyTabListState({
              tabId: EHANDOVER_TAB_IDS.MY_DRAFT_TAB,
              list: revampMyDraftTask,
              next: result?.next,
              start,
              total: result?.total,
            });
          },
          null,
          TAB_LIST_API_TOTAL_ATTEMPTS
        );
      } catch (error) {}
    },
    [applyTabListState, handleFilter, loginInfo.corpID, retryApiCall, revampTaskData, sortOrder]
  );

  // 刷新 Require ACK tab 列表或 total。
  const getAcknowledgeTaskAPI = useCallback(
    async ({ start, search, startDate, endDate, tag, select = FIELD_FROM_BITRIX_API, totalOnly = false }) => {
      try {
        await retryApiCall(
          async () => {
            const { result } = await ehandoverAPI.getAcknowledgeTask(
              {
                select,
                start,
                filter: { ...handleFilter({ search, startDate, endDate, tag }) },
                order: sortOrder,
                corpId: loginInfo.corpID,
              },
              { isHideLoading: true }
            );

            applyTabListState({
              tabId: EHANDOVER_TAB_IDS.REQUIRE_ACK_TAB,
              // Require ACK tab 的 totalOnly 刷新和 Pin tab 一样，只需要 total，
              // 这里也要避免在字段不完整时先做 revampTaskData。
              list: totalOnly ? [] : revampTaskData(result.tasks),
              next: result?.next,
              start,
              total: result?.total,
              totalOnly,
            });
          },
          null,
          TAB_LIST_API_TOTAL_ATTEMPTS
        );
      } catch (error) {}
    },
    [applyTabListState, handleFilter, loginInfo.corpID, retryApiCall, revampTaskData, sortOrder]
  );

  // 用户 check / uncheck task item 后，重新同步 checklist family 的 total 和数据。
  const refreshChecklistTabs = useCallback(
    async ({ search, startDate, endDate, tag, select = FIELD_FROM_BITRIX_API }) => {
      try {
        await refreshChecklistBuckets({ search, startDate, endDate, tag, select });
      } catch (error) {}
    },
    [refreshChecklistBuckets]
  );

  // 统一拿到当前页面里“可见且允许更新 total”的 tab。
  // my draft 不显示 total，所以默认从 total 刷新集合里排除。
  const getVisibleTotalTabIds = useCallback(
    (excludeTabIds = []) => {
      return _.chain(tabData)
        .filter((item) => item?.isShow && ![EHANDOVER_TAB_IDS.MY_DRAFT_TAB, ...excludeTabIds].includes(item?.id))
        .map('id')
        .value();
    },
    [tabData]
  );

  // 按 tabId 批量更新 title total，不更新这些 tab 的列表内容。
  // 这里保留 checklist family 的特殊联动规则，避免打散后续筛选刷新行为。
  const refreshTabTotalsByTabIds = useCallback(
    async ({ tabIds, search, startDate, endDate, tag }) => {
      const visibleTabIds = new Set(getVisibleTotalTabIds());
      const targetTabIds = _.uniq(tabIds).filter((tabId) => visibleTabIds.has(tabId));
      const shouldRefreshChecklistFamily = _.some(targetTabIds, (tabId) => CHECKLIST_FAMILY_TAB_IDS.includes(tabId));

      if (targetTabIds.length === 0) {
        return;
      }

      const commonParams = { search, startDate, endDate, tag };
      const requestList = [];

      if (shouldRefreshChecklistFamily) {
        requestList.push(refreshChecklistBuckets({ ...commonParams, select: INITIAL_TOTAL_SELECT, totalOnly: true }));
      }
      if (_.includes(targetTabIds, EHANDOVER_TAB_IDS.SENT_TAB)) {
        requestList.push(getAllTaskAPI({ ...commonParams, select: INITIAL_TOTAL_SELECT, totalOnly: true }));
      }
      if (_.includes(targetTabIds, EHANDOVER_TAB_IDS.SCHEDULE_TAB)) {
        requestList.push(getPostdayTaskAPI({ ...commonParams, select: INITIAL_TOTAL_SELECT, totalOnly: true }));
      }
      if (_.includes(targetTabIds, EHANDOVER_TAB_IDS.DELETED_TAB)) {
        requestList.push(
          getDeletedTaskAPI({ ...commonParams, select: [...INITIAL_TOTAL_SELECT], totalOnly: true })
        );
      }
      if (_.includes(targetTabIds, EHANDOVER_TAB_IDS.PIN_TAB)) {
        requestList.push(getPinnedTask({ ...commonParams, select: INITIAL_TOTAL_SELECT, totalOnly: true }));
      }
      if (_.includes(targetTabIds, EHANDOVER_TAB_IDS.NOTIFICATION_TAB)) {
        requestList.push(getNotificationList({ ...commonParams, totalOnly: true }));
      }
      if (isLogin && _.includes(targetTabIds, EHANDOVER_TAB_IDS.REQUIRE_ACK_TAB)) {
        requestList.push(getAcknowledgeTaskAPI({ ...commonParams, select: INITIAL_TOTAL_SELECT, totalOnly: true }));
      }

      await Promise.all(requestList);
    },
    [
      getAcknowledgeTaskAPI,
      getAllTaskAPI,
      getDeletedTaskAPI,
      getNotificationList,
      getPinnedTask,
      getPostdayTaskAPI,
      getVisibleTotalTabIds,
      isLogin,
      refreshChecklistBuckets,
    ]
  );

  // 切换 outstanding / completed tab 时，只精准刷新另一个 sibling 的 total。
  // 这条逻辑单独拆出来，是为了避免又复用 checklist family totals 导致当前 active tab 多打一遍接口。
  const refreshChecklistSiblingTotal = useCallback(
    async ({ tabId, search, startDate, endDate, tag }) => {
      if (tabId === EHANDOVER_TAB_IDS.OUTSTANDING_TAB) {
        await getOutstandingTask({ search, startDate, endDate, tag, select: INITIAL_TOTAL_SELECT, totalOnly: true });
      }
      if (tabId === EHANDOVER_TAB_IDS.COMPLETED_TAB) {
        await getCompleteTask({ search, startDate, endDate, tag, select: INITIAL_TOTAL_SELECT, totalOnly: true });
      }
    },
    [getCompleteTask, getOutstandingTask]
  );

  // 只加载当前 active tab 的 list。
  // 首屏、切 tab、search、15 分钟刷新都会复用这个入口，避免同一套选择逻辑散落多处。
  const loadActiveTabList = useCallback(
    async ({ search, startDate, endDate, tag, start }) => {
      const commonParams = { search, startDate, endDate, tag, start };

      switch (currentTabId) {
        case EHANDOVER_TAB_IDS.SENT_TAB:
          await getAllTaskAPI(commonParams);
          break;
        case EHANDOVER_TAB_IDS.CHECKLIST_TAB:
          await getChecklistTaskAPI(commonParams);
          break;
        case EHANDOVER_TAB_IDS.OUTSTANDING_TAB:
          await getOutstandingTask(commonParams);
          break;
        case EHANDOVER_TAB_IDS.COMPLETED_TAB:
          await getCompleteTask(commonParams);
          break;
        case EHANDOVER_TAB_IDS.SCHEDULE_TAB:
          await getPostdayTaskAPI(commonParams);
          break;
        case EHANDOVER_TAB_IDS.DELETED_TAB:
          await getDeletedTaskAPI(commonParams);
          break;
        case EHANDOVER_TAB_IDS.NOTIFICATION_TAB:
          await getNotificationList(commonParams);
          break;
        case EHANDOVER_TAB_IDS.PIN_TAB:
          await getPinnedTask(commonParams);
          break;
        case EHANDOVER_TAB_IDS.MY_DRAFT_TAB:
          if (isLogin) {
            await getMyDraftTask(commonParams);
          }
          break;
        case EHANDOVER_TAB_IDS.REQUIRE_ACK_TAB:
          if (isLogin) {
            await getAcknowledgeTaskAPI(commonParams);
          }
          break;
        default:
          break;
      }
    },
    [
      currentTabId,
      getAcknowledgeTaskAPI,
      getAllTaskAPI,
      getChecklistTaskAPI,
      getCompleteTask,
      getDeletedTaskAPI,
      getMyDraftTask,
      getNotificationList,
      getOutstandingTask,
      getPinnedTask,
      getPostdayTaskAPI,
      isLogin,
    ]
  );

  // 公共筛选变化后，只补刷当前规则表要求联动的 total，不再广播刷新所有 tab list。
  const refreshSelectiveTotalsByActiveTab = useCallback(
    async ({ search, startDate, endDate, tag }) => {
      const targetTabIds = SELECTIVE_TOTAL_REFRESH_MAP[currentTabId] ?? [];

      if (targetTabIds.length === 0) {
        return;
      }

      await refreshTabTotalsByTabIds({ tabIds: targetTabIds, search, startDate, endDate, tag });
    },
    [currentTabId, refreshTabTotalsByTabIds]
  );

  // 15 分钟自动刷新时，当前 active tab 继续刷新 list，其它可见 tab 只刷新 total。
  const refreshAllTabTotals = useCallback(
    async ({ search, startDate, endDate, tag }) => {
      const targetTabIds = getVisibleTotalTabIds([currentTabId]);

      await refreshTabTotalsByTabIds({ tabIds: targetTabIds, search, startDate, endDate, tag });
    },
    [currentTabId, getVisibleTotalTabIds, refreshTabTotalsByTabIds]
  );

  // 首次进入 EHandover 页面时，预取“当前 active tab”和“my draft”之外的其它可见 tab total。
  // 这里只更新标题 total，不覆盖各 tab 当前列表数据。
  const prefetchInitialOtherTabTotals = useCallback(async () => {
    if (!currentTabId) {
      return;
    }

    const visibleTabIds = getVisibleTotalTabIds([currentTabId]);
    if (visibleTabIds.length === 0) {
      return;
    }

    await refreshTabTotalsByTabIds({
      tabIds: visibleTabIds,
      search,
      startDate,
      endDate,
      tag: tagNames,
    });
  }, [currentTabId, endDate, getVisibleTotalTabIds, refreshTabTotalsByTabIds, search, startDate, tagNames]);

  return {
    handleFilter,
    getAcknowledgeTaskAPI,
    getAllTaskAPI,
    getChecklistTaskAPI,
    getCompleteTask,
    getDeletedTaskAPI,
    getMyDraftTask,
    getNotificationList,
    getOutstandingTask,
    getPinnedTask,
    getPostdayTaskAPI,
    loadActiveTabList,
    prefetchInitialOtherTabTotals,
    refreshAllTabTotals,
    refreshChecklistSiblingTotal,
    refreshChecklistTabs,
    refreshSelectiveTotalsByActiveTab,
  };
}
